import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'

import T from 'T'

import ReactivationServiceRow from './ReactivationServiceRow'

import type { ReactivationLocationGroup, ReactivationSelection } from './reactivationSelection'

type ReactivationSelectionListProps = {
  groups: ReactivationLocationGroup[]
  selection: ReactivationSelection
  onToggleLocation: (group: ReactivationLocationGroup) => void
  onToggleService: (group: ReactivationLocationGroup, serviceId: string) => void
}

type LocationHeaderProps = Pick<ReactivationSelectionListProps, 'selection' | 'onToggleLocation'> & { group: ReactivationLocationGroup }

const addressOf = (group: ReactivationLocationGroup) => {
  const { address, locationName } = group.location
  const cityLine = [address?.city, [address?.state, address?.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  return [address?.line1, cityLine].filter(Boolean).join(', ') || locationName || ''
}

const LocationHeader = ({ group, selection, onToggleLocation }: LocationHeaderProps) => {
  const handleChange = useCallback(() => onToggleLocation(group), [group, onToggleLocation])
  const serviceCount = group.services.length

  return (
    <Stack alignItems="center" bgcolor="background.default" direction="row" gap={1} position="sticky" px={1} py={0.5} top={0} zIndex={1}>
      <Checkbox
        checked={!group.isLocationDeactivated || selection.locationIds.has(group.location.locationId)}
        disabled={!group.isLocationDeactivated}
        inputProps={{ 'aria-label': addressOf(group) }}
        size="small"
        onChange={handleChange}
      />
      <Typography flex={1} fontWeight={600} variant="body2">
        {addressOf(group)}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {`${serviceCount} ${(serviceCount === 1 ? T.SERVICE : T.SERVICES).toLowerCase()}`}
      </Typography>
    </Stack>
  )
}

const ReactivationSelectionList = ({ groups, selection, onToggleLocation, onToggleService }: ReactivationSelectionListProps) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      maxHeight: 360,
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 8 },
    }}
  >
    {groups.map(group => (
      <Box key={group.location.locationId} sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}>
        <LocationHeader group={group} selection={selection} onToggleLocation={onToggleLocation} />
        {group.services.map(service => (
          <ReactivationServiceRow
            key={service.configuredServiceId}
            group={group}
            selection={selection}
            service={service}
            onToggleService={onToggleService}
          />
        ))}
      </Box>
    ))}
  </Box>
)

export default ReactivationSelectionList
